<template>
  <v-container fluid class="pa-4 pa-md-6 telemetry-dashboard">
    <!-- Barra Superior / Header Limpio -->
    <v-card flat class="header-card mb-6 pa-4 rounded-lg">
      <v-row align="center" justify="space-between" no-gutters>
        <!-- Título e Identificador del Modo -->
        <v-col cols="12" sm="8" class="d-flex align-center">
          <v-btn
            icon
            small
            depressed
            class="mr-3 action-btn-soft"
            @click="$router.back()"
            title="Volver al menú"
          >
            <v-icon small color="grey darken-2">mdi-arrow-left</v-icon>
          </v-btn>

          <div>
            <div class="d-flex align-center">
              <v-icon
                small
                class="mr-2"
                :color="is_analizador ? 'purple' : (is_temphum_url ? 'teal' : 'primary')"
              >
                {{ is_analizador ? 'mdi-sine-wave' : (is_temphum_url ? 'mdi-water-percent' : 'mdi-thermometer') }}
              </v-icon>
              <h1 class="text-h6 font-weight-bold text--primary mb-0 tracking-tight">
                {{ tituloPagina }}
              </h1>
            </div>
            <div class="caption grey--text text--darken-1 mt-0">
              {{ is_analizador ? 'Monitorización de analizadores de red' : (is_temphum_url ? 'Monitorización combinada de temperatura y humedad' : 'Control y monitorización de temperaturas') }}
            </div>
          </div>
        </v-col>

        <!-- Botones de Acción Global -->
        <v-col cols="12" sm="4" class="d-flex justify-end align-center mt-3 mt-sm-0" v-if="canEdit">
          <input
            type="file"
            ref="excelInput"
            accept=".xlsx, .xls, .csv"
            style="display: none"
            @change="handleFileUpload"
          />

          <v-btn
            v-if="!is_analizador"
            outlined
            small
            color="success darken-1"
            class="mr-2 text-none font-weight-medium rounded-pill btn-soft"
            @click="triggerExcelInput"
          >
            <v-icon left small>mdi-file-excel-outline</v-icon>
            Importar
          </v-btn>

          <v-btn
            depressed
            small
            color="primary"
            class="text-none font-weight-medium rounded-pill px-3"
            @click="openNewTempDialog"
          >
            <v-icon left small>mdi-plus</v-icon>
            Nuevo Registro
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Barra de Búsqueda y Filtros Rápidos -->
    <v-row class="mb-4" dense align="center">
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="searchDescription"
          placeholder="Filtrar por descripción..."
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          flat
          outlined
          hide-details
          class="rounded-lg bg-white custom-search-field"
        ></v-text-field>
      </v-col>
    </v-row>

    <!-- Tabla 1: Variables Principales / Primera Tabla -->
    <v-row class="mb-6" v-if="!is_temphum_url">
      <v-col cols="12">
        <v-card flat outlined class="rounded-lg overflow-hidden border-light">
          <v-card-title class="py-3 px-4 bg-surface-subtle d-flex justify-space-between align-center">
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3 text-uppercase tracking-wider">
              {{ is_analizador ? "Variables Principales" : labelTabla1 }}
            </span>
            <v-chip x-small color="grey lighten-3" class="text-caption font-weight-bold">
              {{ filteredPrimeraTabla.length }} registros
            </v-chip>
          </v-card-title>
          
          <v-divider></v-divider>

          <v-data-table
            :headers="getHeaders1"
            :items="filteredPrimeraTabla"
            class="modern-data-table"
            loading-text="Cargando datos..."
            disable-sort
          >
            <template v-slot:item.description="{ item }">
              <span class="font-weight-medium text--primary">{{ formatDescription(item.description) }}</span>
            </template>
            <template v-slot:item.nave="{ item }">
              <v-chip x-small color="primary lighten-5" class="primary--text font-weight-bold">
                Nave {{ item.nave }}
              </v-chip>
            </template>
            <template v-slot:item.type="{ item }">
              <span class="text-caption grey--text text--darken-2">{{ getZonaName(item) }}</span>
            </template>
            <template v-slot:item.ubicacion="{ item }">
              <span class="text-caption font-weight-medium">{{ item.ubicacion?.ubicacion || '—' }}</span>
            </template>
            <template v-slot:item.value="{ item }">
              <div class="d-flex align-center justify-end font-numeric">
                <span v-if="item.value !== null" class="font-weight-bold text-subtitle-2">
                  {{ item.value?.toFixed(2) }}
                  <span class="text-caption font-weight-regular grey--text">{{ is_analizador ? '' : '%' }}</span>
                </span>
                <span v-else class="text--disabled">—</span>
              </div>
            </template>
            <template v-slot:item.alarm="{ item }">
              <div class="action-btn-group d-flex justify-center">
                <!-- Analizadores -->
                <v-btn
                  v-if="is_analizador"
                  icon
                  x-small
                  class="action-icon-subtle mr-1"
                  @click="openAlarmConfigAnalizador(item)"
                  title="Configurar alarma"
                  :disabled="isManteni()"
                >
                  <v-icon small color="grey darken-1">mdi-cog-outline</v-icon>
                </v-btn>
                <v-btn
                  v-if="is_analizador"
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="openAlarmParamAnalizador(item)"
                  title="Parametrizar alarma"
                  :disabled="isManteni()"
                >
                  <v-icon small color="primary">mdi-pencil-outline</v-icon>
                </v-btn>

                <!-- Control Temperaturas -->
                <v-btn
                  v-if="!is_analizador"
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="openAlarmParam(item)"
                  title="Parametrizar alarma"
                  :disabled="isManteni()"
                >
                  <v-icon small color="primary">mdi-pencil-outline</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.history="{ item }">
              <div class="d-flex justify-center">
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="showHistory(item)"
                  title="Ver historial"
                >
                  <v-icon small color="info">mdi-chart-line</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla 2: Variables Secundarias / Temperaturas & Humedades -->
    <v-row>
      <v-col cols="12">
        <v-card flat outlined class="rounded-lg overflow-hidden border-light">
          <v-card-title class="py-3 px-4 bg-surface-subtle d-flex justify-space-between align-center">
            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3 text-uppercase tracking-wider">
              {{ is_analizador ? "Variables Secundarias" : labelTabla2 }}
            </span>
            <v-chip x-small color="grey lighten-3" class="text-caption font-weight-bold">
              {{ filteredTemperaturas.length }} registros
            </v-chip>
          </v-card-title>
          
          <v-divider></v-divider>

          <v-data-table
            :headers="getHeaders2"
            :items="filteredTemperaturas"
            class="modern-data-table"
            loading-text="Cargando datos..."
            disable-sort
          >
            <template v-slot:item.description="{ item }">
              <span class="font-weight-medium text--primary">{{ formatDescription(item.description) }}</span>
            </template>
            <template v-slot:item.nave="{ item }">
              <v-chip x-small color="primary lighten-5" class="primary--text font-weight-bold">
                Nave {{ item.nave }}
              </v-chip>
            </template>
            <template v-slot:item.type="{ item }">
              <span class="text-caption grey--text text--darken-2">{{ getZonaName(item) }}</span>
            </template>
            <template v-slot:item.ubicacion="{ item }">
              <span class="text-caption font-weight-medium">{{ item.ubicacion?.ubicacion || '—' }}</span>
            </template>
            <template v-slot:item.sala_ambiente="{ item }">
              <span v-if="item.sala_ambiente !== null" class="font-numeric font-weight-medium">
                {{ getTemperaturaAmbiente(item) }} <span class="caption grey--text">°C</span>
              </span>
              <span v-else class="text--disabled">—</span>
            </template>
            <template v-slot:item.value="{ item }">
              <div class="d-flex align-center justify-end font-numeric">
                <span v-if="item.value !== null" class="font-weight-bold text-subtitle-2">
                  {{ item.value?.toFixed(2) }}
                  <span class="text-caption font-weight-regular grey--text">{{ is_analizador ? "" : "°C" }}</span>
                </span>
                <span v-else class="text--disabled">—</span>
              </div>
            </template>
            <template v-slot:item.humidity_value="{ item }">
              <div class="d-flex align-center justify-end font-numeric">
                <span v-if="item.humidity_value !== null" class="font-weight-bold text-subtitle-2 blue--text text--darken-2">
                  {{ item.humidity_value ? item.humidity_value?.toFixed(2) : "---" }}
                  <span class="text-caption font-weight-regular grey--text">{{ is_analizador ? "" : "%" }}</span>
                </span>
                <span v-else class="text--disabled">—</span>
              </div>
            </template>
            <template v-slot:item.alarm="{ item }">
              <div class="action-btn-group d-flex justify-center" v-if="item.alarm">
                <!-- NUEVO BOTÓN: Edición de memorias PLC -->
                <v-btn
                  v-if="canEdit && !is_analizador"
                  icon
                  x-small
                  class="action-icon-subtle mr-1"
                  @click="openEditAddressesDialog(item)"
                  title="Editar memorias de lectura/escritura PLC"
                >
                  <v-icon small color="blue-grey darken-1">mdi-memory</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle mr-1"
                  @click="openAlarmConfig(item)"
                  title="Configurar alarma"
                  :disabled="isManteni() || isProductionN1() || isProduction()"
                >
                  <v-icon small color="grey darken-1">mdi-cog-outline</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="openAlarmParam(item)"
                  title="Parametrizar alarma"
                  :disabled="isManteni() || isProductionN1() || isProduction()"
                >
                  <v-icon small color="primary">mdi-pencil-outline</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.humidity_alarm="{ item }">
              <div class="action-btn-group d-flex justify-center" v-if="item.alarm">
                <!-- NUEVO BOTÓN: Edición de memorias PLC en modo Humedad -->
                <v-btn
                  v-if="canEdit"
                  icon
                  x-small
                  class="action-icon-subtle mr-1"
                  @click="openEditAddressesDialog(item)"
                  title="Editar memorias de lectura/escritura PLC"
                >
                  <v-icon small color="blue-grey darken-1">mdi-memory</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="openSelectableDialog(item)"
                  title="Configurar alarma"
                  :disabled="isManteni() || isProductionN1() || isProduction()"
                >
                  <v-icon small color="grey darken-1">mdi-cog-outline</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.history="{ item }">
              <div class="d-flex justify-center">
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="showHistory(item)"
                  title="Ver historial"
                >
                  <v-icon small color="info">mdi-chart-line</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.humidity_history="{ item }">
              <div class="d-flex justify-center">
                <v-btn
                  v-if="item.humidity_value"
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click="showHistory(item, 2)"
                  title="Ver historial"
                >
                  <v-icon small color="info">mdi-chart-line</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- NUEVO DIALOG: Edición de Memorias PLC (Adaptativo Temps vs TempHum) -->
    <v-dialog v-model="showEditAddressesDialog" max-width="580px" persistent>
      <v-card v-if="editingAddressItem" class="rounded-xl pa-2">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon small color="blue-grey darken-1" class="mr-2">mdi-memory</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Editar Direcciones PLC</span>
        </v-card-title>
        <v-card-subtitle class="caption grey--text">
          {{ editingAddressItem.description }} (Nave {{ editingAddressItem.nave }})
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <!-- CASO 1: TEMPERATURAS (deshab_address y parameter_address) -->
          <template v-if="is_temps_url">
            <div class="sub-panel mb-3">
              <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mb-2 tracking-wider">
                Memorias de Operación
              </div>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="addressForm.parameter_address"
                    label="Memoria Parámetro (parameter_address)"
                    outlined
                    dense
                    hide-details="auto"
                    class="font-numeric mb-3"
                    prepend-inner-icon="mdi-code-tags"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="addressForm.deshab_address"
                    label="Memoria Deshabilitar (deshab_address)"
                    outlined
                    dense
                    hide-details="auto"
                    class="font-numeric"
                    prepend-inner-icon="mdi-toggle-switch-off-outline"
                  />
                </v-col>
              </v-row>
            </div>
          </template>

          <!-- CASO 2: TEMPERATURAS Y HUMEDADES (alarm_tmin, alarm_tmax, alarm_hmin, alarm_hmax) -->
          <template v-if="is_temphum_url">
            <!-- Temperatura -->
            <div class="sub-panel mb-3">
              <div class="d-flex align-center mb-2">
                <v-icon small color="orange darken-2" class="mr-1">mdi-thermometer</v-icon>
                <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wider">
                  Alarmas de Temperatura
                </span>
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="addressForm.alarm_tmin_address"
                    label="Alarma T. Mín (alarm_tmin_address)"
                    outlined
                    dense
                    hide-details="auto"
                    class="font-numeric"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="addressForm.alarm_tmax_address"
                    label="Alarma T. Máx (alarm_tmax_address)"
                    outlined
                    dense
                    hide-details="auto"
                    class="font-numeric"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Humedad -->
            <div class="sub-panel">
              <div class="d-flex align-center mb-2">
                <v-icon small color="blue darken-2" class="mr-1">mdi-water-percent</v-icon>
                <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wider">
                  Alarmas de Humedad
                </span>
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="addressForm.alarm_hmin_address"
                    label="Alarma H. Mín (alarm_hmin_address)"
                    outlined
                    dense
                    hide-details="auto"
                    class="font-numeric"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="addressForm.alarm_hmax_address"
                    label="Alarma H. Máx (alarm_hmax_address)"
                    outlined
                    dense
                    hide-details="auto"
                    class="font-numeric"
                  />
                </v-col>
              </v-row>
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="px-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn
            text
            small
            class="text-none font-weight-medium"
            @click="showEditAddressesDialog = false"
            :disabled="savingAddresses"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            depressed
            small
            class="text-none font-weight-medium px-4 rounded-pill"
            @click="saveAddresses"
            :loading="savingAddresses"
          >
            Guardar Direcciones
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Configuración de alarmas estándar -->
    <v-dialog v-model="showAlarmConfDialog" persistent max-width="750px">
      <v-card class="rounded-xl overflow-hidden">
        <v-progress-linear
          v-if="loading_update_alarm"
          indeterminate
          color="primary"
          height="3"
        />
        <AlarmConfigurationForm
          :incomingAlarm="updating_alarm_configuration"
          :notificationTypes="notification_types"
          @close="saveAlarm"
          @cancel="showAlarmConfDialog = false"
        />
      </v-card>
    </v-dialog>

    <!-- Dialog: Configuración de alarmas de analizadores -->
    <v-dialog v-model="showAnalizadorAlarmConfDialog" persistent max-width="750px">
      <v-card class="rounded-xl overflow-hidden">
        <v-progress-linear
          v-if="loading_update_alarm_analizator"
          indeterminate
          color="primary"
          height="3"
        />
        <AnalizadorAlarmConfigurationForm
          :incomingAlarm="updating_analizador_alarm_configuration"
          :notificationTypes="notification_types"
          @close="saveAnalizadorAlarm"
          @cancel="showAnalizadorAlarmConfDialog = false"
        />
      </v-card>
    </v-dialog>

    <!-- Dialog: Parametrización de Alarmas (Modernizado con Micro-Cards) -->
    <v-dialog v-model="showAlarmParamDialog" max-width="560px">
      <v-card v-if="alarm_params" class="rounded-xl pa-2">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon color="primary" class="mr-2" small>mdi-tune-vertical</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ alarm_params.description }}</span>
        </v-card-title>
        <v-card-subtitle class="caption grey--text">
          Ajuste los límites y estados de activación del canal
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <!-- Valor Actual -->
          <div class="sub-panel mb-3 d-flex align-center justify-space-between">
            <span class="text-caption font-weight-medium grey--text text--darken-2">Valor actual registrado:</span>
            <div class="d-flex align-center">
              <span class="font-numeric font-weight-bold mr-2 text-subtitle-2">
                {{ alarm_params.value }} {{ alarm_params.description.toLowerCase().includes('temperatura') ? '°C' : '%' }}
              </span>
              <v-icon x-small color="grey">mdi-lock-outline</v-icon>
            </div>
          </div>

          <!-- Valor de Alarma -->
          <div class="sub-panel mb-3">
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="text-caption font-weight-medium grey--text text--darken-2">Valor de alarma (Setpoint):</span>
            </div>
            <v-text-field
              v-model.number="alarm_params.mod_value"
              dense
              outlined
              type="number"
              class="font-numeric"
              :suffix="alarm_params.description.toLowerCase().includes('temperatura') ? '°C' : '%'"
              :rules="[
                v => (alarm_params.description.toLowerCase().includes('temperatura') ? (v <= 60 || 'El máximo permitido es 60°C') : true)
              ]"
              :error="alarm_params.description.toLowerCase().includes('temperatura') && alarm_params.mod_value > 60"
              hide-details="auto"
            />
            <div
              v-if="alarm_params.description.toLowerCase().includes('temperatura')" 
              class="d-flex align-center mt-2 caption" 
              :class="alarm_params.mod_value > 60 ? 'red--text' : 'grey--text text--darken-1'"
            >
              <v-icon x-small class="mr-1" :color="alarm_params.mod_value > 60 ? 'red' : 'blue'">
                mdi-information-outline
              </v-icon>
              <span>Los sensores actuales solo leen hasta 60°C</span>
            </div>
          </div>

          <!-- Deshabilitar Canal -->
          <div class="sub-panel mb-3 d-flex align-center justify-space-between" v-if="!alarm_params.is_variacion">
            <div>
              <div class="text-caption font-weight-medium grey--text text--darken-2">Deshabilitar canal</div>
              <div class="caption grey--text">Permite anular temporalmente lecturas erróneas</div>
            </div>
            <div class="d-flex align-center">
              <v-switch
                v-model="alarm_params.anular"
                inset
                dense
                hide-details
                @click="anular()"
                :label="alarm_params.anular ? 'Activado' : 'Desactivado'"
                :disabled="selected_alarm && selected_alarm.deshab_address === 'D1'"
                class="mt-0"
              />
              <v-icon 
                v-if="selected_alarm && selected_alarm.deshab_address === 'D1'" 
                small 
                class="ml-2" 
                color="orange"
                title="Este canal no permite deshabilitación"
              >
                mdi-alert-circle-outline
              </v-icon>
            </div>
          </div>

          <!-- Variación brusca -->
          <div class="sub-panel d-flex align-center justify-space-between" v-if="alarm_params.description.toLowerCase().includes('temperatura') && is_temps_url">
            <span class="text-caption font-weight-medium grey--text text--darken-2">Variación brusca permitida:</span>
            <div class="d-flex align-center">
              <span class="font-numeric font-weight-bold mr-2 text-subtitle-2">{{ alarm_params?.variacion }} °C</span>
              <v-icon x-small color="grey">mdi-lock-outline</v-icon>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-3">
          <v-spacer />
          <v-btn text small class="text-none font-weight-medium" @click="closeParamDialog">Cerrar</v-btn>
          <v-btn 
            depressed
            small
            color="success darken-1" 
            class="text-none font-weight-medium px-4 rounded-pill"
            @click="guardarValor()"
            :disabled="
              !alarm_params.mod_value || 
              (alarm_params.description.toLowerCase().includes('temperatura') && alarm_params.mod_value > 60)
            "
          >
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Parametrización Analizador -->
    <v-dialog v-model="showAnalizadorAlarmParamDialog" max-width="500px" persistent>
      <v-card v-if="analizador_alarm_params" class="rounded-xl pa-2">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon small color="primary" class="mr-2">mdi-tune</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Parámetros de Alarma</span>
        </v-card-title>
        <v-card-subtitle class="caption grey--text">
          {{ analizador_alarm_params.description }}
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <v-form>
            <div class="sub-panel mb-3">
              <v-text-field
                v-model="analizador_alarm_params.humbral"
                label="Umbral de Disparo"
                type="number"
                outlined
                dense
                hide-details
                class="font-numeric"
                :rules="[]"
              ></v-text-field>
            </div>

            <div class="sub-panel mb-3">
              <v-text-field
                v-model="analizador_alarm_params.tiempo"
                label="Retardo / Tiempo (segundos)"
                type="number"
                outlined
                dense
                hide-details
                class="font-numeric"
              ></v-text-field>
            </div>

            <div class="sub-panel" v-if="analizador_alarm_params.variacion !== undefined">
              <v-text-field
                v-model="analizador_alarm_params.variacion"
                label="Porcentaje de Variación (%)"
                type="number"
                outlined
                dense
                hide-details
                class="font-numeric"
              ></v-text-field>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn text small class="text-none font-weight-medium" @click="closeAnalizadorParamDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            depressed
            small
            @click="guardarValorAnalizador"
            class="text-none font-weight-medium px-4 rounded-pill"
          >
            Guardar Parámetros
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Crear Nuevo Registro / Sensor -->
    <v-dialog v-model="showNewTempDialog" max-width="750px">
      <v-card class="rounded-xl pa-2">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon left color="primary" small>{{ is_temphum_url ? 'mdi-water-percent' : 'mdi-thermometer' }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ dialogTitle }}</span>
        </v-card-title>
        <v-card-subtitle class="caption grey--text">
          Verifique que la variable y memorias asociadas existan en la base de datos o PLC.
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <v-container fluid class="pa-0">
            <!-- Datos Generales -->
            <div class="sub-panel mb-4">
              <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mb-2 tracking-wider">
                Datos Identificativos
              </div>
              <v-row dense>
                <v-col cols="12" md="8">
                  <v-text-field v-model="newTemp.description" label="Descripción *" outlined dense required hide-details="auto" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-combobox v-model="newTemp.nave" :items="availableNaves.map(n => n.value)" label="Nave *" outlined dense required hide-details="auto" />
                </v-col>
                <v-col cols="12" md="12" class="mt-2">
                  <v-text-field v-model="newTemp.deshab_address" label="Memoria Deshabilitar (deshab_address) *" outlined dense hide-details="auto" />
                </v-col>
              </v-row>
            </div>

            <!-- MODO: Solo Temperaturas (temps) -->
            <template v-if="is_temps_url">
              <div class="sub-panel mb-4">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mb-2 tracking-wider">
                  Mapeo de Memorias PLC
                </div>
                <v-row dense>
                  <v-col cols="12" md="6"><v-text-field v-model="newTemp.var_address" label="Memoria Lectura *" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="6"><v-text-field v-model="newTemp.alarm_address" label="Memoria Alarma *" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="6" class="mt-2"><v-text-field v-model="newTemp.parameter_address" label="Memoria Parámetro *" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="6" class="mt-2"><v-text-field v-model="newTemp.deshab_address" label="Memoria Deshabilitar *" outlined dense hide-details="auto" /></v-col>
                </v-row>
              </div>

              <div class="sub-panel">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mb-2 tracking-wider">
                  Ubicación & Tipo
                </div>
                <v-row dense align="center">
                  <v-col cols="12" md="6">
                    <v-select v-model="newTemp.id_plc" :items="availableTypes" item-text="text" label="PLC / Zona *" return-object outlined dense required hide-details="auto" />
                  </v-col>
                  <v-col cols="12" md="6" v-if="!is_analizador">
                    <v-combobox v-model="newTemp.id_ubicacion" :items="filteredUbicacionesPorPlc" item-text="ubicacion" label="Ubicación *" outlined dense hide-selected :delimiters="['']" hide-details="auto">
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title class="text-caption font-weight-medium">{{ item.ubicacion }}</v-list-item-title>
                          <v-list-item-subtitle class="caption grey--text">PLC ID: {{ item.id_zona }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-combobox>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox v-model="newTemp.is_variacion" label="¿Es Alarma por Variación Brusca?" dense hide-details class="mt-2" />
                  </v-col>
                </v-row>
              </div>
            </template>

            <!-- MODO: Temp + Humedad (temphum) -->
            <template v-if="is_temphum_url">
              <!-- Sub-sección Temperatura -->
              <div class="sub-panel mb-4">
                <div class="d-flex align-center mb-2">
                  <v-icon small color="orange darken-2" class="mr-1">mdi-thermometer</v-icon>
                  <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wider">
                    Límites de Temperatura
                  </span>
                </div>
                <v-row dense>
                  <v-col cols="12" md="4"><v-text-field v-model="newTemp.temp_address" label="Memoria Lectura" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4"><v-text-field v-model="newTemp.alarm_tmin_address" label="Alarma Mín." outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4"><v-text-field v-model="newTemp.alarm_tmax_address" label="Alarma Máx." outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4" offset-md="4" class="mt-2"><v-text-field v-model="newTemp.param_tmin_address" label="Parám. Mín." outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4" class="mt-2"><v-text-field v-model="newTemp.param_tmax_address" label="Parám. Máx." outlined dense hide-details="auto" /></v-col>
                </v-row>
              </div>

              <!-- Sub-sección Humedad -->
              <div class="sub-panel mb-4">
                <div class="d-flex align-center mb-2">
                  <v-icon small color="blue darken-2" class="mr-1">mdi-water-percent</v-icon>
                  <span class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 tracking-wider">
                    Límites de Humedad
                  </span>
                </div>
                <v-row dense>
                  <v-col cols="12" md="4"><v-text-field v-model="newTemp.humidity_address" label="Memoria Lectura" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4"><v-text-field v-model="newTemp.alarm_hmin_address" label="Alarma Mín." outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4"><v-text-field v-model="newTemp.alarm_hmax_address" label="Alarma Máx." outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4" offset-md="4" class="mt-2"><v-text-field v-model="newTemp.param_hmin_address" label="Parám. Mín." outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="4" class="mt-2"><v-text-field v-model="newTemp.param_hmax_address" label="Parám. Máx." outlined dense hide-details="auto" /></v-col>
                </v-row>
              </div>

              <!-- PLC & Ubicación -->
              <div class="sub-panel">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mb-2 tracking-wider">
                  Ubicación & PLC
                </div>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-select v-model="newTemp.id_plc" :items="availableTypes" item-text="text" label="PLC / Zona *" return-object outlined dense required hide-details="auto" />
                  </v-col>
                  <v-col cols="12" md="6" v-if="!is_analizador">
                    <v-combobox v-model="newTemp.id_ubicacion" :items="filteredUbicacionesPorPlc" item-text="ubicacion" label="Ubicación *" outlined dense hide-selected :delimiters="['']" hide-details="auto">
                      <template v-slot:item="{ item }">
                        <v-list-item-content>
                          <v-list-item-title class="text-caption font-weight-medium">{{ item.ubicacion }}</v-list-item-title>
                          <v-list-item-subtitle class="caption grey--text">PLC ID: {{ item.id_zona }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>
              </div>
            </template>

            <!-- MODO: Analizadores -->
            <template v-if="is_analizador">
              <div class="sub-panel">
                <div class="text-caption font-weight-bold text-uppercase grey--text text--darken-2 mb-2 tracking-wider">
                  Configuración Analizador
                </div>
                <v-row dense>
                  <v-col cols="12" md="6"><v-text-field v-model="newTemp.alarm_address" label="Inicio Analizador (alarm_address) *" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" md="6"><v-text-field v-model="newTemp.var_address" label="Variable Analizador (var_address) *" outlined dense hide-details="auto" /></v-col>
                  <v-col cols="12" class="mt-2">
                    <v-checkbox v-model="newTemp.is_principal" label="¿Es Variable Principal?" hide-details dense />
                  </v-col>
                </v-row>
              </div>
            </template>
          </v-container>
        </v-card-text>

        <v-card-actions class="px-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn text small class="text-none font-weight-medium" @click="showNewTempDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed small class="text-none font-weight-medium px-4 rounded-pill" @click="saveNewTemp" :loading="savingNewTemp">
            Guardar Registro
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Selección de Alarma Temp/Hum -->
    <v-dialog v-model="showAlarmSelectorDialog" max-width="520px">
      <v-card class="rounded-xl pa-2">
        <v-card-title class="d-flex align-center pb-1">
          <v-icon left color="primary" small>mdi-bell-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">Selección de Alarma</span>
        </v-card-title>
        <v-card-subtitle class="caption grey--text">
          Seleccione el canal de alarma a gestionar
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <v-list class="pa-0">
            <v-list-item
              v-for="(alarm, key) in selectable_config_alarm"
              :key="key"
              @click="selectAlarm(alarm.alarm)"
              class="sub-panel mb-2 rounded-lg list-item-selectable"
              :class="{ 'border-primary': updating_alarm_configuration?.alarm_id === alarm.alarm_id }"
            >
              <v-list-item-icon class="my-auto mr-3">
                <v-icon small :color="getAlarmIconColor(key)">
                  {{ getAlarmIcon(key) }}
                </v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title class="text-body-2 font-weight-medium">{{ alarm.alarm.text_description }}</v-list-item-title>
                <v-list-item-subtitle class="caption grey--text">
                  ID: {{ alarm.alarm.alarm_id }} • {{ alarm.alarm.notification_type_description }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action class="d-flex flex-row align-center my-auto">
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle mr-1"
                  @click.stop="openAlarmConfig(alarm)"
                  title="Configurar alarma"
                  :disabled="isManteni() || isProductionN1() || isProduction()"
                >
                  <v-icon small color="grey darken-1">mdi-cog-outline</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  class="action-icon-subtle"
                  @click.stop="openAlarmParam(alarm)"
                  title="Parametrizar alarma"
                  :disabled="isManteni() || isProductionN1() || isProduction()"
                >
                  <v-icon small color="primary">mdi-pencil-outline</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <div v-if="selectable_config_alarm && Object.keys(selectable_config_alarm).length === 0" class="text-center py-4 grey--text caption">
              No hay alarmas configuradas disponibles
            </div>
          </v-list>
        </v-card-text>

        <v-card-actions class="px-4 pb-2">
          <v-spacer></v-spacer>
          <v-btn text small class="text-none font-weight-medium" @click="showAlarmSelectorDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Vista Previa Carga Masiva Excel -->
    <v-dialog v-model="showExcelPreviewDialog" max-width="1100px" persistent>
      <v-card class="rounded-xl overflow-hidden pa-2">
        <v-card-title class="d-flex align-center justify-space-between pb-1">
          <div class="d-flex align-center">
            <v-icon color="success darken-1" class="mr-2" small>mdi-file-excel-outline</v-icon>
            <span class="text-subtitle-1 font-weight-bold">
              Vista previa: Importación masiva ({{ parsedExcelData.length }} registros)
            </span>
          </div>
          <v-chip x-small color="grey lighten-4" class="grey--text text--darken-3 font-weight-medium">
            {{ is_temphum_url ? 'Temp + Humedad' : 'Temperaturas' }}
          </v-chip>
        </v-card-title>

        <v-card-subtitle class="caption grey--text">
          Revise minuciosamente las filas detectadas antes de confirmar la inserción masiva.
        </v-card-subtitle>

        <v-card-text class="pt-2">
          <v-alert v-if="parsedExcelData.length === 0" type="warning" dense text class="rounded-lg caption">
            No se encontraron filas con datos estructurados válidos en el archivo seleccionado.
          </v-alert>

          <v-card v-else flat outlined class="rounded-lg overflow-hidden border-light">
            <v-data-table
              :headers="previewHeaders"
              :items="parsedExcelData"
              :items-per-page="5"
              class="modern-data-table"
              dense
            >
              <template v-slot:item.active="{ item }">
                <v-chip x-small :color="item.active ? 'green lighten-5' : 'red lighten-5'" :class="item.active ? 'green--text font-weight-bold' : 'red--text font-weight-bold'">
                  {{ item.active ? 'Sí' : 'No' }}
                </v-chip>
              </template>
              <template v-slot:item.is_variacion="{ item }">
                <v-chip x-small :color="item.is_variacion ? 'orange lighten-5' : 'grey lighten-4'" :class="item.is_variacion ? 'orange--text text--darken-3 font-weight-bold' : 'grey--text font-weight-bold'">
                  {{ item.is_variacion ? 'Sí' : 'No' }}
                </v-chip>
              </template>
              <template v-slot:item.is_humedad="{ item }">
                <v-chip x-small :color="item.is_humedad ? 'blue lighten-5' : 'grey lighten-4'" :class="item.is_humedad ? 'blue--text font-weight-bold' : 'grey--text font-weight-bold'">
                  {{ item.is_humedad ? 'Sí' : 'No' }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card>
        </v-card-text>

        <v-card-actions class="px-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn text small class="text-none font-weight-medium" @click="cancelExcelPreview" :disabled="savingBulkData">
            Cancelar
          </v-btn>
          <v-btn
            color="success darken-1"
            depressed
            small
            @click="confirmBulkUpload"
            :loading="savingBulkData"
            :disabled="parsedExcelData.length === 0"
            class="text-none font-weight-medium px-4 rounded-pill"
          >
            <v-icon left small>mdi-cloud-upload-outline</v-icon>
            Confirmar y Guardar ({{ parsedExcelData.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { Buffer } from "buffer";
import MultiVariableLineChart from "@/components/utils/MultiVariableLineChart.vue";
import AlarmConfigurationForm from "@/components/forms/AlarmConfigurationForm.vue";
import AnalizadorAlarmConfigurationForm from "../forms/AnalizadorAlarmConfigurationForm.vue";
import * as XLSX from "xlsx"; // Librería SheetJS

export default {
  name: "TemperaturasTable",
  components: { MultiVariableLineChart, AlarmConfigurationForm, AnalizadorAlarmConfigurationForm },
  data() {
    return {
      searchDescription: "",
      filterType: null,
      filterNave: null,
      filterUbicacion: null,

      notification_types: [],
      temperaturas: [],
      variaciones: [],
      humedades: [],
      
      //CONTROL DE TEMPERATURAS(TEMPERATURAS)
      headers: [
        { text: "Descripción", value: "description", sortable: false },
        { text: "Nave", value: "nave", sortable: false, align: "start" },
        { text: "Temperatura (°C)", value: "value", sortable: false, align: "end" },
        { text: "Alarma", value: "alarm", sortable: false, align: "center" },
        { text: "Histórico", value: "history", sortable: false, align: "center" }
      ],
      //CONTROL DE TEMPERATURAS(VARIACIONES)
      headersVariacion: [
        { text: "Descripción", value: "description", sortable: false },
        { text: "Nave", value: "nave", sortable: false, align: "start" },
        { text: "Alarma", value: "alarm", sortable: false, align: "center" },
      ],

      //TEMPERATURA Y HUMEDADES(TEMPERATURAS)
      headersTemperatura: [
        { text: "Descripción", value: "description", sortable: false },
        { text: "Nave", value: "nave", sortable: false, align: "start" },
        { text: "Temperatura (°C)", value: "value", sortable: false, align: "end" },
        { text: "Histórico", value: "history", sortable: false, align: "center" },
        { text: "Humedad (%)", value: "humidity_value", sortable: false, align: "end" },
        { text: "Alarma", value: "humidity_alarm", sortable: false, align: "center" },
        { text: "Histórico", value: "humidity_history", sortable: false, align: "center" }
      ],

      //ANALIZADORES(PRINCIPALES)
      headersAnalizadoresPrincipales: [
        { text: "Descripción", value: "description", sortable: false },
        { text: "Nave", value: "nave", sortable: false, align: "start" },
        { text: "Valor actual", value: "value", sortable: false, align: "end" },
        { text: "Alarma", value: "alarm", sortable: false, align: "center" },
        { text: "Histórico", value: "history", sortable: false, align: "center" }
      ],
      //ANALIZADORES(SECUNDARIOS)
      headersAnalizadoresSecundarios: [
        { text: "Descripción", value: "description", sortable: false },
        { text: "Nave", value: "nave", sortable: false, align: "start" },
        { text: "Valor actual", value: "value", sortable: false, align: "end" },
        { text: "Histórico", value: "history", sortable: false, align: "center" }
      ],

      showAlarmParamDialog: false,
      showAlarmConfDialog: false,
      showAlarmSelectorDialog: false,
      
      loading_update_alarm: false,
      loadingTemperaturas: true,
      loading_update_alarm_analizator: false,
      
      selectedItem: null,
      alarm_params: null,
      selected_alarm: null,
      updating_alarm_configuration: null,
      selectable_config_alarm: {},
      selectedParentItem: null,

      showAnalizadorAlarmConfDialog: false,
      updating_analizador_alarm_configuration: null,
      showAnalizadorAlarmParamDialog: false,
      selected_analizador_alarm: null,
      analizador_alarm_params: null,

      showNewTempDialog: false,
      savingNewTemp: false,
      newTemp: {
        description: "",
        nave: null,
        id_ubicacion: null,
        var_address: "", 
        alarm_address: "",
        parameter_address: "",
        deshab_address: "",
        is_variacion: false,
        is_principal: false,
        temp_address: "",
        alarm_tmin_address: "",
        alarm_tmax_address: "",
        param_tmin_address: "",
        param_tmax_address: "",
        humidity_address: "",
        alarm_hmin_address: "",
        param_hmin_address: "",
        alarm_hmax_address: "",
        param_hmax_address: "",
      },

      // --- PROPIEDADES PARA EDICIÓN DE DIRECCIONES PLC ---
      showEditAddressesDialog: false,
      savingAddresses: false,
      editingAddressItem: null,
      addressForm: {
        parameter_address: "",
        deshab_address: "",
        alarm_tmin_address: "",
        alarm_tmax_address: "",
        alarm_hmin_address: "",
        alarm_hmax_address: "",
      },

      availableTypes: [],

      showExcelPreviewDialog: false,
      parsedExcelData: [],
      savingBulkData: false,
    };
  },
  computed: {
    ...mapState("nodered", ["client", "showing_history_address"]),
    canEdit() { return this.isDeveloper(); },

    url(){ return `variables/${this.$route.path.split('/')[1]}`; },
    urlNave(){ return this.$route.params.nave },
    is_analizador(){ return this.url.includes("analizador"); },
    is_temphum_url() { return this.url.includes('hum'); },
    is_temps_url() { return !this.is_temphum_url && !this.is_analizador; },
    
    dialogTitle() {
      if (this.is_temphum_url) return "Nueva Temp. y Humedad";
      if (this.is_analizador) return "Nuevo Analizador";
      return "Nueva Temperatura";
    },
    tituloPagina(){
      return this.is_temphum_url 
        ? `TEMPERATURAS y HUMEDADES${ this.urlNave == 0 ? "": ` Salas N${this.urlNave}` }` 
        : this.is_analizador ? `Analizador${ this.urlNave == 0 ? " Nave general": ` Nave N${this.urlNave}` }` 
                               : 'CONTROL SEGURIDAD TEMPERATURA ARMARIOS';
    },
    labelTabla1(){
      return this.is_temphum_url 
        ? 'Humedades' 
        : 'Alarmas por variación brusca';
    },
    labelTabla2(){
      return this.is_temphum_url 
        ? 'Temperaturas y Humedades' 
        : 'Temperaturas de armarios';
    },
    variacionLabel(){
      return this.is_temphum_url 
        ? '¿Es humedad?' 
        : this.is_analizador ? '¿Es variable principal?'
                              :'¿Es alarma por variación brusca?';
    },
    getHeaders1(){
      return this.is_temphum_url 
        ? this.headersHumedad
        : this.is_analizador ? this.headersAnalizadoresPrincipales 
                              : this.headersVariacion;
    },
    getHeaders2(){
      return this.is_temphum_url 
        ? this.headersTemperatura
        : this.is_analizador ? this.headersAnalizadoresSecundarios 
                            : this.headers;
    },

    filteredVariaciones(){
      return this.variaciones.filter(item => {
        return !this.filterType || item.type.name === this.filterType.value.name;
      })
    },
    filteredHumedades(){
      return this.humedades.filter(item => {
        const matchesDescription = !this.searchDescription ||
          item.description.toLowerCase().includes(this.searchDescription.toLowerCase());
        const matchesType = !this.filterType || item.type.name === this.filterType.value.name;
        const matchesNave = !this.filterNave || item.nave === this.filterNave;
        const matchUbi = !this.filterUbicacion || item.ubicacion.id_ubicacion == this.filterUbicacion.id_ubicacion;
        return matchesDescription && matchesType && matchesNave && matchUbi;
      });
    },

    filteredPrimeraTabla(){
      return this.url.includes("hum") ?
        this.filteredHumedades :
        this.filteredVariaciones;
    },

    filteredTemperaturas() {
      return this.temperaturas.filter(item => {
        const matchesDescription = !this.searchDescription ||
          item.description.toLowerCase().includes(this.searchDescription.toLowerCase());
        const matchesType = !this.filterType || item.type.name === this.filterType.value.name;
        const matchesNave = !this.filterNave || item.nave === this.filterNave;
        const matchUbi = !this.filterUbicacion || item.ubicacion.id_ubicacion == this.filterUbicacion.id_ubicacion;
        return matchesDescription && matchesType && matchesNave && matchUbi;
      });
    },

    previewHeaders() {
      if (this.is_temphum_url) {
        return [
          { text: "Descripción", value: "description" },
          { text: "Nave", value: "nave" },
          { text: "Mem. Temp", value: "temp_address" },
          { text: "Alarma T. Min", value: "alarm_tmin_address" },
          { text: "Alarma T. Max", value: "alarm_tmax_address" },
          { text: "ID Alarma T. Min", value: "id_alarm_tmin" },
          { text: "ID Alarma T. Max", value: "id_alarm_tmax" },
          { text: "Mem. Hum", value: "hum_address" },
          { text: "Alarma H. Min", value: "alarm_hmin_address" },
          { text: "Alarma H. Max", value: "alarm_hmax_address" },
          { text: "ID Alarma H. Min", value: "id_alarm_hmin" },
          { text: "ID Alarma H. Max", value: "id_alarm_hmax" },
          { text: "Mem. Deshab.", value: "deshab_address" },
          { text: "ID Ubicación", value: "id_ubicacion" },
          { text: "¿Es Humedad?", value: "is_humedad" },
          { text: "Activo", value: "active" },
        ];
      } else {
        return [
          { text: "Descripción", value: "description" },
          { text: "Nave", value: "nave" },
          { text: "Mem. Lectura (var)", value: "var_address" },
          { text: "Mem. Alarma", value: "alarm_address" },
          { text: "Mem. Parámetro", value: "parameter_address" },
          { text: "Mem. Deshab.", value: "deshab_address" },
          { text: "ID Alarma", value: "id_alarm" },
          { text: "ID Ambiente", value: "id_ambiente" },
          { text: "ID Ubicación", value: "id_ubicacion" },
          { text: "¿Variación?", value: "is_variacion" },
          { text: "Activo", value: "active" },
        ];
      }
    },

    filteredTemperaturasOnCreate(){
      return this.temperaturas.filter(item => {
        const matchesType = !this.newTemp.id_plc || item.type.name === this.newTemp.id_plc.value.name;
        const matchesNave = !this.newTemp.nave || item.nave === this.newTemp.nave;
        const matchUbi = !this.newTemp.id_ubicacion || item.ubicacion.id_ubicacion == this.newTemp.id_ubicacion.id_ubicacion;
        return matchesType && matchesNave && matchUbi;
      });
    },
    filteredUbicacionesPorPlc() {
      if (!this.newTemp.id_plc) return this.availableUbicaciones;
      const plcId = this.newTemp.id_plc.value?.id_plc || this.newTemp.id_plc.id_plc;
      return this.availableUbicaciones.filter(u => u.id_zona == plcId);
    },

    availableNaves() {
      const naves = [...new Set(this.temperaturas.map(item => item.nave))];
      return naves
        .filter(n => n != null && n !== "")
        .map(nave => ({ text: `Nave ${nave}`, value: nave }));
    },
    availableUbicaciones() {
      const ubicacionesUnicas = [...new Map(
          this.temperaturas
            .filter(t => t.ubicacion)
            .map(t => [t.ubicacion.id_ubicacion, t.ubicacion])
        ).values()];

        return this.filterType ? ubicacionesUnicas.filter((ubi) => ubi.id_zona == this.filterType.value.id_plc) :
              this.newTemp.id_plc ? ubicacionesUnicas.filter((ubi) => ubi.id_zona == this.newTemp.id_plc.value.id_plc) : ubicacionesUnicas;
    }
  },
  watch: {
    "client.connected": {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.doSubscribe("PLC2R");
          this.doSubscribe("PLC4R");
          this.doSubscribe("PLC5R");
          this.doSubscribe("PLC6R");
          this.doSubscribe("PARAMS_TEMP");
          this.doSubscribe("AQLVN");
          this.doSubscribe("ANALIZADOR_ALARM_PARAM");
          if(!this.is_analizador){
            this.client.removeAllListeners("message");
            this.client.on("message", (topic, message) => {
              if (topic == "PARAMS_TEMP") {
                const decodedMessage = JSON.parse(message);
                if (this.selected_alarm && this.selected_alarm.alarm && !this.showAlarmParamDialog) {
                  this.alarm_params = decodedMessage;
                  this.alarm_params.description = this.selected_alarm.is_variacion ? this.selected_alarm.description : this.selected_alarm.alarm.text_description;
                  this.alarm_params.is_variacion = this.selected_alarm.is_variacion;
                  this.showAlarmParamDialog = true;
                }
              }
              if (topic === "PLC2R" || topic === "PLC4R" || topic == "PLC5R" || topic == "PLC6R") {
                try {
                  const decodedMessage = JSON.parse(message);
                  const { MemoryArea: memoryArea, Address: firstAddress } = decodedMessage.fins.request.address;
                  let currentAddress = firstAddress;
                  const data = decodedMessage.read.data;
                  for (let i = 0; i < data.length; i += 4) {
                    if (i + 3 >= data.length) break;
                    const transformedAddress = `${memoryArea}${currentAddress}`;
                    let variableToChange = this.temperaturas.concat(this.humedades).find(t => t.var_address === transformedAddress && topic == t.topic_read);
                    if (variableToChange) {
                      const A = data[i];
                      const B = data[i + 1];
                      const C = data[i + 2];
                      const D = data[i + 3];
                      const newbuf = Buffer.from([C, D, A, B]);
                      variableToChange.value = newbuf.readFloatBE();
                      if (variableToChange.var_address == this.showing_history_address)
                        this.addHistoryValue(variableToChange);
                    }

                    let tempVarForHumidity = this.temperaturas.find(t => t.parameter_address === transformedAddress && topic === t.topic_read);

                    if (tempVarForHumidity) {
                      const A = data[i];
                      const B = data[i + 1];
                      const C = data[i + 2];
                      const D = data[i + 3];
                      const newbuf = Buffer.from([C, D, A, B]);
                      tempVarForHumidity.humidity_value = newbuf.readFloatBE();

                      if (tempVarForHumidity.parameter_address === this.showing_history_address_humidity) {
                        this.addHistoryValueForHumidity(tempVarForHumidity);
                      }
                    }
                    currentAddress += 2;
                  }
                } catch (e) {
                  console.error("Error al procesar mensaje MQTT:", e);
                }
              }
            });
          } else {
            this.client.removeAllListeners("message");
            this.client.on("message", (topic, message) => {
              if (topic == "ANALIZADOR_ALARM_PARAM") {
                const decodedMessage = JSON.parse(message);
                if (this.selected_analizador_alarm && this.selected_analizador_alarm.alarm && !this.showAnalizadorAlarmParamDialog) {
                  this.analizador_alarm_params = decodedMessage;
                  this.analizador_alarm_params.description = this.selected_analizador_alarm.alarm.name;
                  this.showAnalizadorAlarmParamDialog = true;
                }
              }
              if (topic === "AQLVN") {
                try {
                  const decodedMessage = JSON.parse(message);
                  const variableToChange = this.variaciones.concat(this.temperaturas).filter((a) => `${a.alarm_address}.${a.var_address}` === decodedMessage.formatted_address)[0];
                  if(variableToChange) {
                    variableToChange.value = parseFloat(decodedMessage.value);
                  }
                } catch (e) {
                  console.error("Error al procesar mensaje MQTT:", e);
                }
              }
            });
          }
        }
      }
    },
  },
  async mounted() {
    try {
      await this.getConfiguration();
    } catch (e) {
      console.error("error al recoger datos de alarmas:", e);
    } finally {
      this.createConnection();
    }
  },
  beforeDestroy() {
    this.doUnsubscribe("PLC2R");
    this.doUnsubscribe("PLC4R");
    this.doUnsubscribe("PLC5R");
    this.doUnsubscribe("PLC6R");
    this.doUnsubscribe("PARAMS_TEMP");
    this.doUnsubscribe("AQLVN");
    this.doUnsubscribe("ANALIZADOR_ALARM_PARAM");
  },
  methods: {
    ...mapActions(["getData", "putData", "postData", "setSelectedPageToBack"]),
    ...mapActions("nodered", [
      "doPublish",
      "doSubscribe",
      "createConnection",
      "doUnsubscribe",
      "setShowingHistoryAddress",
      "addHistoryValue"
    ]),
    ...mapGetters(["isDeveloper", "isRamos", "isAdmin", "isManteni", "isProduction", "isProductionN1"]),

    async getConfiguration() {
      const { temp } = await this.getData({ path: this.url });
      this.availableTypes = await this.getData({ path: "variables/plcs" });

      if (this.url.includes("hum")) {
        const applyFilters = (items, isHumedad) => {
          return items
            .filter(item => {
              const matchesType = (isHumedad ? item.is_humedad === true : item.is_humedad === false);
              const matchesNave = this.urlNave == 0 || item.nave == this.urlNave;
              return matchesType && matchesNave;
            })
            .map(item => ({
              ...item,
              value: null,
              history: true,
            }));
        };
        this.temperaturas = applyFilters(temp, false);
      } else {
        if(this.is_analizador){
          const applyFilters = (items, isPrincipal) => {
            return items
              .filter(item => {
                const matchesType = (isPrincipal ? item.is_principal === true : item.is_principal === false);
                const matchesNave = this.urlNave == 0 ? item.nave == "General" : item.nave == this.urlNave;
                return matchesType && matchesNave;
              })
              .map(item => ({
                ...item,
                value: null,
                history: true,
              }));
          };
          this.variaciones = applyFilters(temp, true);
          this.temperaturas = applyFilters(temp, false);
        } else {
          const applyFilters = (items, isVariacion) => {
            return items
              .filter(item => {
                const matchesType = (isVariacion ? item.is_variacion === true : item.is_variacion === false);
                const matchesNave = this.urlNave == 0 ? item.nave == "General" : item.nave == this.urlNave;
                return matchesType && matchesNave;
              })
              .map(item => ({
                ...item,
                value: null,
                history: true,
              }));
          };
          this.variaciones = applyFilters(temp, true);
          this.temperaturas = applyFilters(temp, false);
        }
      }

      const { notification_types } = await this.getData({ path: "alarms/configuration" });
      this.notification_types = notification_types;
    },

    setRunOrderValue(value, address, topic, description) {
      this.doPublish({ topic, message: { address, value }, description });
    },

    getTemperaturaAmbiente(item) {
      return this.temperaturas.find(temp => temp.id == item.sala_ambiente)?.value?.toFixed(2);
    },
    getZonaName(item){
      return item.type.name == "ARMARIOS" ? "PRODUCCIÓN" : item.type.name;
    },
    formatDescription(desc) {
      if (!desc) return '';
      let result = desc.toLowerCase();
      result = result.charAt(0).toUpperCase() + result.slice(1);
      result = result.replace(/\[([^\]]+)\]$/, (match, content) => `[${content.toUpperCase()}]`);
      return result;
    },

    // --- MÉTODOS PARA EDITAR DIRECCIONES PLC ---
    openEditAddressesDialog(item) {
      this.editingAddressItem = item;
      if (this.is_temphum_url) {
        this.addressForm = {
          alarm_tmin_address: item.alarm_tmin_address || "",
          alarm_tmax_address: item.alarm_tmax_address || "",
          alarm_hmin_address: item.alarm_hmin_address || "",
          alarm_hmax_address: item.alarm_hmax_address || "",
        };
      } else {
        this.addressForm = {
          parameter_address: item.parameter_address || "",
          deshab_address: item.deshab_address || "",
        };
      }
      this.showEditAddressesDialog = true;
    },

    async saveAddresses() {
      this.savingAddresses = true;
      try {
        let payload = {};
        if (this.is_temphum_url) {
          payload = {
            alarm_tmin_address: this.addressForm.alarm_tmin_address,
            alarm_tmax_address: this.addressForm.alarm_tmax_address,
            alarm_hmin_address: this.addressForm.alarm_hmin_address,
            alarm_hmax_address: this.addressForm.alarm_hmax_address,
          };
        } else {
          payload = {
            parameter_address: this.addressForm.parameter_address,
            deshab_address: this.addressForm.deshab_address,
          };
        }

        await this.putData({
          path: this.url,
          params: this.editingAddressItem.id,
          body: {
            id: this.editingAddressItem.id,
            ...payload
          }
        });

        this.$toast?.success?.("Direcciones PLC actualizadas correctamente");
        this.showEditAddressesDialog = false;
        await this.getConfiguration();
      } catch (error) {
        console.error("Error al actualizar direcciones PLC:", error);
        this.$toast?.error?.("No se pudieron actualizar las direcciones PLC");
      } finally {
        this.savingAddresses = false;
      }
    },

    showHistory(item, type = 1) {
      if (type == 2) {
        this.temperaturas = this.temperaturas
        .filter(item => item.parameter_address && item.parameter_address.trim() !== '')
        .map(item => ({
          ...item,
          var_address: item.parameter_address
        }));

        if (item.parameter_address && item.parameter_address.trim() !== '') {
          item.var_address = item.parameter_address;
        }
      }
      this.$router.push({
        path: '/history/1',
        query: {
          variables: this.is_analizador ? 
                  this.temperaturas.concat(this.variaciones) : this.temperaturas,
          selected_var: item,
          is_analizador: this.is_analizador,
        }
      });
    },

    selectAlarm(alarm) {
      this.updating_alarm_configuration = alarm;
    },
    confirmAlarmSelection() {
      if (this.updating_alarm_configuration) {
        this.showAlarmConfDialog = true;
      }
    },
    getAlarmIcon(key) {
      if (key.includes('tmin')) return 'mdi-thermometer-low';
      if (key.includes('tmax')) return 'mdi-thermometer-high';
      if (key.includes('hmin')) return 'mdi-water-minus';
      if (key.includes('hmax')) return 'mdi-water-plus';
      return 'mdi-bell';
    },
    getAlarmIconColor(key) {
      if (key.includes('t')) return 'error';
      if (key.includes('h')) return 'blue';
      return 'grey';
    },
    
    buildSelectableAlarms(item) {
      if (!item) return;
      this.selectable_config_alarm = {
        ...(item.alarm?.t_min && { alarm_tmin: { alarm: item.alarm.t_min, parameter_address: item.alarm_tmin_address, deshab_address: item.deshab_address, topic_read: item.topic_read } }),
        ...(item.alarm?.t_max && { alarm_tmax: { alarm: item.alarm.t_max, parameter_address: item.alarm_tmax_address, deshab_address: item.deshab_address, topic_read: item.topic_read } }),
        ...(item.humidity_alarm?.h_min && { alarm_hmin: { alarm: item.humidity_alarm.h_min, parameter_address: item.alarm_hmin_address, deshab_address: item.deshab_address, topic_read: item.topic_read } }),
        ...(item.humidity_alarm?.h_max && { alarm_hmax: { alarm: item.humidity_alarm.h_max, parameter_address: item.alarm_hmax_address, deshab_address: item.deshab_address, topic_read: item.topic_read } }),
      };
    },

    openSelectableDialog(item) {
      this.selectedParentItem = item;
      this.buildSelectableAlarms(item);
      this.showAlarmSelectorDialog = true;
    },

    openAlarmConfig(item) {
      this.updating_alarm_configuration = item.alarm;
      this.showAlarmConfDialog = true;
    },
    async saveAlarm({ alarm_id, ...alarm_configuration }) {
      try {
        this.loading_update_alarm = true;
        await this.putData({ path: "alarms", params: alarm_id, body: { ...alarm_configuration } });
        await this.getConfiguration();
        if (this.selectedParentItem) {
          const updatedItem = this.temperaturas.concat(this.variaciones).find(i => i.id === this.selectedParentItem.id); 
          if (updatedItem) {
            this.selectedParentItem = updatedItem;
            this.buildSelectableAlarms(updatedItem);
          }
        }
        this.loading_update_alarm = false;
        this.showAlarmConfDialog = false;
      } catch (error) {
        console.error(error);
      }
    },

    openAlarmConfigAnalizador(item){
      this.updating_analizador_alarm_configuration = item.alarm;
      this.showAnalizadorAlarmConfDialog = true;
    },
    async saveAnalizadorAlarm(alarm){
      alarm.id = alarm.alarm_id;
      try{
        this.loading_update_alarm_analizator = true;
        await this.putData({ path: "alarms/analizadores_update", body: alarm });
        await this.getConfiguration();
        this.loading_update_alarm_analizator = false;
        this.showAnalizadorAlarmConfDialog = false;
      } catch(e){
        console.error("Error al actualizar alarma:", e);
      }
    },

    openAlarmParam(item) {
      this.selected_alarm = item;
      this.doPublish({
        topic: `${item.topic_read}_TEMP`,
        message: { var_address: item.parameter_address, deshab_address: item.deshab_address },
      });
    },
    guardarValor() {
      this.setRunOrderValue(
        parseInt(this.alarm_params.mod_value),
        this.selected_alarm.parameter_address,
        `${this.selected_alarm.topic_read.slice(0, -1)}WREAL`,
        `Parametrización de alarma de ${this.selected_alarm.description} modificada`
      );
      this.showAlarmParamDialog = false;
      this.alarm_params = null;
    },
    anular() {
      this.setRunOrderValue(
        this.alarm_params.anular,
        this.selected_alarm.deshab_address,
        `${this.selected_alarm.topic_read.slice(0, -1)}WBOOL`
      );
    },
    closeParamDialog() {
      this.showAlarmParamDialog = false;
      this.selected_alarm = null;
    },

    openAlarmParamAnalizador(item){
      this.selected_analizador_alarm = item;
      this.doPublish({ topic: `${item.alarm_address}.${item.var_address}`, message: `${item.alarm_address}.${item.var_address}`});
    },
    guardarValorAnalizador() {
      this.doPublish({
        topic:`NEW_${this.selected_analizador_alarm.alarm_address}.${this.selected_analizador_alarm.var_address}`,
        message: this.analizador_alarm_params,
        description: `Parametro de variable "${this.selected_analizador_alarm.alarm_address}.${this.selected_analizador_alarm.var_address}" del analizador ${this.selected_analizador_alarm.nave} modificado`
      });
      this.showAnalizadorAlarmParamDialog = false;
      this.selected_analizador_alarm = null;
    },
    closeAnalizadorParamDialog() {
      this.showAnalizadorAlarmParamDialog = false;
      this.selected_analizador_alarm = null;
    },

    openNewTempDialog() {
      this.filterNave = null;
      this.filterType = null;
      this.filterUbicacion = null;

      this.newTemp = {
          description: "",
          nave: null,
          id_ubicacion: null,
          var_address: "", 
          alarm_address: "",
          parameter_address: "",
          deshab_address: "",
          is_variacion: false,
          is_principal: false,
          temp_address: "",
          alarm_tmin_address: "",
          param_tmin_address: "",
          alarm_tmax_address: "",
          param_tmax_address: "",
          humidity_address: "",
          alarm_hmin_address: "",
          param_hmin_address: "",
          alarm_hmax_address: "",
          param_hmax_address: "",
      };
      this.showNewTempDialog = true;
    },
    async saveNewTemp() {
      if (!this.is_analizador) {
        if (!this.newTemp.id_plc || !this.newTemp.id_ubicacion || !this.newTemp.description || !this.newTemp.nave) {
          this.$toast?.error("Seleccione PLC y Ubicación");
          return;
        }
      }

      this.savingNewTemp = true;

      try {
        let payload = {
          description: this.newTemp.description,
          nave: typeof this.newTemp.nave === 'object' ? this.newTemp.nave.value : this.newTemp.nave
        };

        if (!this.is_analizador) {
          const plcId = this.newTemp.id_plc.value?.id_plc || this.newTemp.id_plc.id_plc;
          let ubicacionFinal;
          if (typeof this.newTemp.id_ubicacion === 'string') {
            ubicacionFinal = {
              nombre: this.newTemp.id_ubicacion,
              id_plc: plcId,
              is_new: true
            };
          } else {
            ubicacionFinal = this.newTemp.id_ubicacion.id_ubicacion;
          }

          if (this.is_temphum_url) {
            payload.temp_address = this.newTemp.temp_address;
            payload.alarm_tmin_address = this.newTemp.alarm_tmin_address;
            payload.alarm_tmax_address = this.newTemp.alarm_tmax_address;
            payload.param_tmin_address = this.newTemp.param_tmin_address;
            payload.param_tmax_address = this.newTemp.param_tmax_address;

            payload.hum_address = this.newTemp.humidity_address;
            payload.alarm_hmin_address = this.newTemp.alarm_hmin_address;
            payload.alarm_hmax_address = this.newTemp.alarm_hmax_address;
            payload.param_hmin_address = this.newTemp.param_hmin_address;
            payload.param_hmax_address = this.newTemp.param_hmax_address;

            payload.id_ubicacion = ubicacionFinal;
            payload.id_plc = plcId;
            payload.deshab_address = this.newTemp.deshab_address;
          } else {
            payload.var_address = this.newTemp.var_address;
            payload.alarm_address = this.newTemp.alarm_address;
            payload.parameter_address = this.newTemp.parameter_address;
            payload.deshab_address = this.newTemp.deshab_address;
            payload.is_variacion = this.newTemp.is_variacion;
            payload.id_ubicacion = ubicacionFinal;
            payload.id_plc = plcId;
          }
        } else {
          payload.alarm_address = this.newTemp.alarm_address;
          payload.var_address = this.newTemp.var_address;
          payload.is_principal = this.newTemp.is_principal;
        }

        await this.postData({
          path: this.url,
          body: payload
        });
        await this.getConfiguration();
        this.showNewTempDialog = false;
      } catch (error) {
        console.error("Error al guardar:", error);
      } finally {
        this.savingNewTemp = false;
      }
    },

    triggerExcelInput() {
      this.$refs.excelInput.click();
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          if (!rows || rows.length === 0) {
            this.$toast?.error?.("El archivo Excel está vacío") || alert("El archivo Excel está vacío");
            return;
          }

          const clean = (val) => (val !== undefined && val !== null ? String(val).trim() : "");
          const toBool = (val) => {
            if (val === undefined || val === null || val === "") return false;
            const str = String(val).trim().toLowerCase();
            return str === "true" || str === "1" || str === "si" || str === "sí" || val === 1 || val === true;
          };

          const startIndex = typeof rows[0]?.[0] === "string" && rows[0][0].toLowerCase().trim() === "description" ? 1 : 0;
          const parsedList = [];

          for (let i = startIndex; i < rows.length; i++) {
            const row = rows[i];
            if (!row || row.length === 0 || (!row[0] && !row[1])) continue;

            if (this.is_temphum_url) {
              parsedList.push({
                description: clean(row[0]),
                temp_address: clean(row[1]),
                alarm_tmin_address: clean(row[2]),
                hum_address: clean(row[3]),
                alarm_tmax_address: clean(row[4]),
                nave: row[5] !== undefined ? clean(row[5]) : "",
                id_alarm_tmin: row[6] !== undefined && row[6] !== "" ? row[6] : null,
                active: row[7] !== undefined && row[7] !== "" ? toBool(row[7]) : true,
                id_ubicacion: row[8] !== undefined && row[8] !== "" ? row[8] : null,
                is_humedad: toBool(row[9]),
                id_alarm_hmin: row[10] !== undefined && row[10] !== "" ? row[10] : null,
                alarm_hmin_address: clean(row[11]),
                alarm_hmax_address: clean(row[12]),
                id_alarm_tmax: row[13] !== undefined && row[13] !== "" ? row[13] : null,
                id_alarm_hmax: row[14] !== undefined && row[14] !== "" ? row[14] : null,
                deshab_address: clean(row[15]),
              });
            } else {
              parsedList.push({
                description: clean(row[0]),
                var_address: clean(row[1]),
                alarm_address: clean(row[2]),
                parameter_address: clean(row[3]),
                deshab_address: clean(row[4]),
                nave: row[5] !== undefined ? clean(row[5]) : "",
                id_alarm: row[6] !== undefined && row[6] !== "" ? row[6] : null,
                active: row[7] !== undefined && row[7] !== "" ? toBool(row[7]) : true,
                id_ambiente: row[8] !== undefined && row[8] !== "" ? row[8] : null,
                id_ubicacion: row[9] !== undefined && row[9] !== "" ? row[9] : null,
                is_variacion: toBool(row[10]),
              });
            }
          }

          if (parsedList.length === 0) {
            this.$toast?.error?.("No se encontraron filas con datos válidos") || alert("No se encontraron filas válidas");
            return;
          }

          this.parsedExcelData = parsedList;
          this.showExcelPreviewDialog = true;
        } catch (error) {
          console.error("Error al procesar el Excel:", error);
          this.$toast?.error?.("Error al leer el archivo Excel") || alert("Error al leer el archivo Excel");
        } finally {
          event.target.value = "";
        }
      };

      reader.readAsArrayBuffer(file);
    },

    cancelExcelPreview() {
      this.showExcelPreviewDialog = false;
      this.parsedExcelData = [];
    },

    async confirmBulkUpload() {
      this.savingBulkData = true;
      try {
        await this.postData({
          path: `${this.url}/bulk`,
          body: { items: this.parsedExcelData }
        });

        this.$toast?.success?.("Registros importados con éxito");
        this.showExcelPreviewDialog = false;
        this.parsedExcelData = [];
        await this.getConfiguration();
      } catch (error) {
        console.error("Error en la subida masiva:", error);
        this.$toast?.error?.("Error al guardar los registros");
      } finally {
        this.savingBulkData = false;
      }
    }
  }
};
</script>

<style scoped>
.font-numeric {
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
}

.tracking-tight {
  letter-spacing: -0.5px;
}
.tracking-wider {
  letter-spacing: 0.5px;
}

.header-card {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.border-light {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.border-primary {
  border: 1px solid var(--v-primary-base) !important;
}

.bg-surface-subtle {
  background-color: #fcfdfe;
}

.custom-search-field ::v-deep .v-input__control > .v-input__slot {
  background-color: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  min-height: 40px !important;
}

.btn-soft {
  background-color: #ffffff;
  border-width: 1px;
}

.action-btn-soft {
  background-color: rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}
.action-btn-soft:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.action-icon-subtle {
  background-color: #f7f9fa;
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;
}
.action-icon-subtle:hover {
  background-color: #edf2f7;
  transform: translateY(-1px);
}

.sub-panel {
  background-color: #f8fafc;
  border: 1px solid #edf2f7;
  border-radius: 8px;
  padding: 12px 14px;
}

.list-item-selectable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.list-item-selectable:hover {
  background-color: #edf2f7;
}

.modern-data-table ::v-deep table {
  border-collapse: collapse;
}

.modern-data-table ::v-deep th {
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  color: #64748b !important;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #edf2f7 !important;
  background-color: #f8fafc !important;
  height: 44px !important;
}

.modern-data-table ::v-deep td {
  font-size: 0.825rem !important;
  border-bottom: 1px solid #f1f5f9 !important;
  height: 48px !important;
}

.modern-data-table ::v-deep tr:hover:not(.v-data-table__expanded__content) {
  background-color: #f8fafc !important;
}
</style>